/**
 * Date utility functions for the time picker feature.
 * All functions work with local dates (no UTC shift issues).
 */

/**
 * Alle Tage zwischen zwei ISO-Daten (inklusive).
 * @param from "2026-05-29"
 * @param to "2026-05-31"
 * @returns ["2026-05-29", "2026-05-30", "2026-05-31"]
 */
export function getDatesInRange(from: string, to: string): string[] {
  const dates: string[] = []
  const current = new Date(from + 'T00:00:00')
  const end = new Date(to + 'T00:00:00')

  while (current <= end) {
    dates.push(toLocalISOString(current))
    current.setDate(current.getDate() + 1)
  }

  return dates
}

export function monthsInRange(from: string, to: string): { year: number; month: number }[] {
  const start = new Date(from + 'T00:00:00')
  const end = new Date(to + 'T00:00:00')
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return []
  const fromDate = start <= end ? start : end
  const toDate = start <= end ? end : start
  const months: { year: number; month: number }[] = []
  const cursor = new Date(fromDate.getFullYear(), fromDate.getMonth(), 1)
  const last = new Date(toDate.getFullYear(), toDate.getMonth(), 1)
  while (cursor <= last) {
    months.push({ year: cursor.getFullYear(), month: cursor.getMonth() + 1 })
    cursor.setMonth(cursor.getMonth() + 1)
  }
  return months
}

export function uniqueLessonDates(lessons: { date?: string | null }[]): string[] {
  return [...new Set(lessons.map(l => l.date).filter((d): d is string => !!d))].sort()
}

/**
 * Prüft ob ein Datum im Bereich liegt (inklusive).
 */
export function isDateInRange(date: string, from: string, to: string): boolean {
  const d = new Date(date + 'T00:00:00').getTime()
  return d >= new Date(from + 'T00:00:00').getTime() && d <= new Date(to + 'T00:00:00').getTime()
}

/**
 * Datum formatieren für Anzeige (DD.MM.)
 */
export function formatDateShort(date: string): string {
  const d = new Date(date + 'T00:00:00')
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`
}

/**
 * Datum formatieren für Anzeige (DD.MM.YYYY)
 */
export function formatDateLong(date: string): string {
  const d = new Date(date + 'T00:00:00')
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`
}

/**
 * Von/Bis als lesbaren String
 * → "29.05. – 31.05.2026 (3 Tage)"
 */
export function formatRange(from: string, to: string): string {
  const days = getDatesInRange(from, to).length
  if (from === to) return `${formatDateLong(from)} (1 Tag)`

  const fromShort = formatDateShort(from)
  const toLong = formatDateLong(to)
  return `${fromShort} – ${toLong} (${days} Tage)`
}

/**
 * Wochentag ermitteln
 * → "Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"
 */
export function getWeekday(date: string): string {
  const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa']
  return days[new Date(date + 'T00:00:00').getDay()] ?? 'So'
}

/**
 * Prüft ob ein Datum ein Wochenende ist
 */
export function isWeekend(date: string): boolean {
  const day = new Date(date + 'T00:00:00').getDay()
  return day === 0 || day === 6
}

/**
 * Zwei ISO-Daten vergleichen
 * @returns -1 if a < b, 0 if a === b, 1 if a > b
 */
export function compareDates(a: string, b: string): number {
  const ta = new Date(a + 'T00:00:00').getTime()
  const tb = new Date(b + 'T00:00:00').getTime()
  return ta < tb ? -1 : ta > tb ? 1 : 0
}

/**
 * Safe date from local input (avoids UTC shift)
 */
export function toLocalISOString(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function isoMonday(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  const wd = d.getDay()
  d.setDate(d.getDate() + (wd === 0 ? -6 : 1 - wd))
  return toLocalISOString(d)
}

export function addDaysIso(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00')
  d.setDate(d.getDate() + days)
  return toLocalISOString(d)
}

export function todayLocalISO(now: Date = new Date()): string {
  return toLocalISOString(now)
}

export function isPastDate(date: string, today: string = todayLocalISO()): boolean {
  return date < today
}

export function isLessonInPast(
  lessonDate: string,
  endTime?: string | null,
  now: Date = new Date(),
): boolean {
  const today = todayLocalISO(now)
  if (lessonDate < today) return true
  if (lessonDate > today) return false
  if (!endTime) return false
  const parts = endTime.split(':').map(Number)
  const hours = parts[0]
  const minutes = parts[1] ?? 0
  const seconds = parts[2] ?? 0
  if (hours === undefined || Number.isNaN(hours)) return false
  const end = new Date(now)
  end.setHours(hours, minutes, seconds, 0)
  return end.getTime() < now.getTime()
}
